import { useState } from "react";
import { Box } from "@analyticsMD/quil";
import { css } from "@emotion/css";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Link from "@mui/material/Link";
import { common, grey, red, indigo, purple } from "@mui/material/colors";
import { PURPLE_TINT } from "./malnutrition-colors";
import CollapsibleSection from "../shared/collapsible-section";

import type { FC, ReactNode } from "react";
import type { PatientWeightHistory } from "../../types/Malnutrition";

const BLUE_ICON_BG = indigo[500];

const styles = {
  subCard: css`
    background: ${grey[50]};
    border-radius: 0.5rem;
    margin: 0 0.75rem 0.625rem;
    padding: 0.25rem 1rem 0.25rem 1rem;
  `,
  tableHead: css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 0.5rem 0;
    border-bottom: 1px solid ${grey[200]};
    color: ${grey[700]};
    font-weight: 600;
    font-size: 0.8125rem;
  `,
  tableRow: css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 0.5rem 0;
    font-size: 0.875rem;
    color: ${grey[800]};
    align-items: center;
  `,
  anchorRow: css`
    background: ${PURPLE_TINT};
    margin: 0 -0.75rem;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    position: relative;
    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0.25rem;
      bottom: 0.25rem;
      width: 3px;
      background: ${purple[500]};
      border-radius: 1px;
    }
  `,
  rangeSelector: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    color: ${grey[700]};
    font-size: 0.8125rem;
  `,
  trendLine: css`
    color: ${red[800]};
    font-weight: 700;
    font-size: 0.9375rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-bottom: 0.75rem;
  `,
  showMore: css`
    display: inline-block;
    margin-top: 0.5rem;
    font-size: 0.875rem;
  `,
};

const sxStyles = {
  subHeading: {
    fontSize: "0.8125rem",
    fontWeight: 700,
    color: grey[700],
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    display: "flex",
    alignItems: "center",
  },
  subIcon: {
    backgroundColor: BLUE_ICON_BG,
    color: common.white,
    borderRadius: "50%",
    padding: "0.25rem",
    fontSize: "1.125rem",
    marginRight: "0.5rem",
  },
};

const SubCardIcon: FC = () => <MapOutlinedIcon sx={sxStyles.subIcon} />;

const Trend: FC<{ percent: number; months: number }> = ({
  percent,
  months,
}) => (
  <Box className={styles.trendLine}>
    <ArrowDownwardIcon sx={{ fontSize: "1rem" }} />
    {percent}% over ~{months} months
  </Box>
);

const TrendChip: FC<{ percent: number; months: number }> = ({
  percent,
  months,
}) => (
  <Box
    component="span"
    sx={{
      color: red[800],
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
      gap: "0.25rem",
    }}
  >
    <ArrowDownwardIcon sx={{ fontSize: "0.9rem" }} />
    {percent} % over ~{months} months
  </Box>
);

interface RecentWeightsCardProps {
  history: PatientWeightHistory;
}

const RecentWeightsCard: FC<RecentWeightsCardProps> = ({ history }) => {
  // Default the comparison span to the full range (oldest -> newest); readings
  // arrive newest-first. The user can re-pick either endpoint from any reading.
  const [fromDate, setFromDate] = useState(
    history.readings[history.readings.length - 1]?.date,
  );
  const [toDate, setToDate] = useState(history.readings[0]?.date);
  const [showAll, setShowAll] = useState(false);

  const previewCount = 6;
  const visible = showAll
    ? history.readings
    : history.readings.slice(0, previewCount);
  const hiddenCount = history.readings.length - previewCount;

  return (
    <CollapsibleSection
      defaultOpen
      hideSummaryWhenOpen
      title="RECENT WEIGHTS"
      icon={<SubCardIcon />}
      summary={
        <TrendChip
          percent={history.trend_percent}
          months={history.trend_months}
        />
      }
      headingTextSx={sxStyles.subHeading}
      outerClassName={styles.subCard}
    >
      <Trend percent={history.trend_percent} months={history.trend_months} />
      <Box className={styles.rangeSelector}>
        <span>FROM</span>
        <Select
          size="small"
          value={fromDate}
          onChange={(e) => setFromDate(String(e.target.value))}
          sx={{ fontSize: "0.8125rem", height: "1.75rem" }}
        >
          {history.readings.map((r, idx) => (
            <MenuItem
              key={idx}
              value={r.date}
              sx={{ fontSize: "0.8125rem" }}
            >
              {r.weight_lb} lb · {r.date}
            </MenuItem>
          ))}
        </Select>
        <ArrowForwardIcon sx={{ fontSize: "0.875rem" }} />
        <span>TO</span>
        <Select
          size="small"
          value={toDate}
          onChange={(e) => setToDate(String(e.target.value))}
          sx={{ fontSize: "0.8125rem", height: "1.75rem" }}
        >
          {history.readings.map((r, idx) => (
            <MenuItem
              key={idx}
              value={r.date}
              sx={{ fontSize: "0.8125rem" }}
            >
              {r.weight_lb} lb · {r.date}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box className={styles.tableHead}>
        <span>Date</span>
        <span>Weight</span>
        <span>Source</span>
      </Box>
      {visible.map((r, idx) => {
        const isSelectedRow = r.date === fromDate || r.date === toDate;
        return (
          <Box
            key={idx}
            className={`${styles.tableRow}${isSelectedRow ? " " + styles.anchorRow : ""}`}
          >
            <span>{r.date}</span>
            <span>{r.weight_lb} lb</span>
            <span>{r.source}</span>
          </Box>
        );
      })}
      {!showAll && hiddenCount > 0 && (
        <Link
          component="button"
          underline="hover"
          onClick={() => setShowAll(true)}
          className={styles.showMore}
        >
          Show {hiddenCount} more
        </Link>
      )}
    </CollapsibleSection>
  );
};

interface SupportingDataSectionProps {
  weights: PatientWeightHistory;
}

const SupportingDataHeader: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      fontSize: "0.8125rem",
      fontWeight: 700,
      color: grey[700],
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      padding: "0.75rem 1.5rem 0.5rem",
    }}
  >
    {children}
  </Box>
);

const SupportingDataSection: FC<SupportingDataSectionProps> = ({ weights }) => {
  // Nothing to show if there are no readings (e.g. all filtered out server-side);
  // avoid an empty card with two blank selects and an empty table.
  if (!weights.readings.length) return null;
  return (
    <Box>
      <SupportingDataHeader>Supporting clinical data</SupportingDataHeader>
      <RecentWeightsCard history={weights} />
    </Box>
  );
};

export default SupportingDataSection;
export { RecentWeightsCard };
